import React from 'react'
import { observer } from 'mobx-react-lite'
import {
    Text,
    Icon,
    FloatingMenu,
    MenuItem,
    MenuDivider,
    Checkbox
} from '@evertel/web/ui'
import { APIDataDocumentACL } from '@evertel/types'
import { DocumentACLsController } from '@evertel/document'

export interface ACLButtonProps {
    attachId?: string
    acl: APIDataDocumentACL
    onChangeACL: (access: string, acl: APIDataDocumentACL) => void
    documentACLController: DocumentACLsController
    disabled?: boolean
}

export const ACLButton: React.FC<ACLButtonProps> = observer(({
    attachId,
    acl,
    documentACLController,
    onChangeACL,
    disabled
}) => {

    const isOwner = acl?.accessType === DocumentACLsController.accessTypes.OWNER

    const humanizeAccessType = () => {
        if (!acl) return 'Add Access'
        if (acl.accessType === DocumentACLsController.accessTypes.OWNER) return 'Owner'
        if (acl.accessType === DocumentACLsController.accessTypes.EDIT_AND_SHARE) return 'Edit, Share'
        if (acl.accessType === DocumentACLsController.accessTypes.EDIT) return 'Edit'
        if (acl.accessType === DocumentACLsController.accessTypes.READ_AND_SHARE) return 'View, Share'
        return 'View'
    }

    if (isOwner) {
        return (
            <Text
                size={12}
                italic
                className="me-4">
                {humanizeAccessType()}
            </Text>
        )
    }

    return (
        <FloatingMenu
            attachId={attachId}
            label={humanizeAccessType()}
            renderLabel={() => (
                <span className="text-nowrap">
                    {humanizeAccessType()}
                    <Icon type="solid" name="caret-down" className="ms-2" />
                </span>
            )}
            className="btn btn-link py-0 px-1"
        >
            <MenuItem
                label="View Only"
                onClick={() => onChangeACL('read', acl)}
                disabled={disabled}
            >
                <div className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                        <Checkbox
                            radio
                            checked={documentACLController?.isViewer(acl.accessType)}
                        />
                        <Text
                            bold
                            color="primary"
                            className="ms-2">
                            View Only
                        </Text>
                    </div>
                    <small
                        className="ps-4 mt-1">
                        All members in this room can view but not edit
                    </small>
                </div>
            </MenuItem>
            <MenuItem
                label="View and Edit"
                onClick={() => onChangeACL('edit', acl)}
                disabled={disabled}
            >
                <div className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                        <Checkbox
                            radio
                            checked={documentACLController?.isEditor(acl.accessType)}
                        />
                        <Text
                            bold
                            color="primary"
                            className="ms-2"
                        >
                            View and Edit
                        </Text>
                    </div>
                    <small
                        className="ms-4 mt-1">
                        All members in this room can view and edit
                    </small>
                </div>
            </MenuItem>
            <MenuItem
                label="Share"
                onClick={() => onChangeACL('share', acl)}
                disabled={disabled}
            >
                <div className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                        <Checkbox
                            checked={documentACLController?.canShareDocument(acl.accessType)}
                        />
                        <Text
                            bold
                            color="primary"
                            className="ms-2"
                        >
                            Share
                        </Text>
                    </div>
                    <small
                        className="ps-4 mt-1">
                        All members in this room can share this document with others
                    </small>
                </div>
            </MenuItem>
            {(acl.id && !disabled) && (
                <MenuItem
                    label="Remove access"
                    onClick={() => onChangeACL('delete', acl)}
                    icon={{
                        name: 'trash',
                        color: 'danger'
                    }}
                    className="text-danger"
                />
            )}
        </FloatingMenu>
    )
})
